<template>
  <div ref="layoutRef" :class="['xy-list-item-layout', directionClass]" :key="refreshIndex">
    <template v-for="(item, index) in listLength" :key="getListKey(index)">
      <div v-if="showItems(index)" :class="['xy-list-item-layout__content list-layout', props.element.uuid]">
        <xyComponent
          v-for="element in elements"
          :element="cloneDeep(element)"
          :key="element.uuid + '_' + getListKey(index)"
          :language="props.language"
          :list-index="indexJoint(index)"
          :list-item-data="getListItemData(index)" />
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, onBeforeMount, computed, watch, nextTick } from 'vue'
import { info, defaultProps } from './config'
import xyComponent from '@/packages/components/xy-component.vue'
import { addListCss } from '@/packages/plugins/hook/useCss'
import { clearListClickDataSource } from '@/packages/plugins/hook/useDataSource'
import useAppStore from '@/h5/stores/app'
import { cloneDeep } from 'lodash'

defineOptions({ name: info.name })
const props = defineProps(defaultProps)

const appStore = useAppStore ? useAppStore() : null
const layoutRef = ref(null)
const elements = ref([])
const refreshIndex = ref(0)
const layoutClassMap = {
  vertical: 'xy-list-item-layout--vertical',
  horizontal: 'xy-list-item-layout--horizontal',
  grid: 'xy-list-item-layout--grid'
}

const directionClass = computed(() => {
  return layoutClassMap[props.direction]
})
// 列表长度
const listLength = ref(0)

const getListKey = (index) => {
  return props.listIndex ? `${props.listIndex.index},${index}` : index
}

/** 下标拼接 */
const indexJoint = (index) => {
  const dataSource = props.element.dataSource.list
  // 绑定数据源, 同数据源索引, 拼接数据源
  const keys = dataSource?.source[0]?.keys || ''
  const sourceKeys = props.listIndex?.source?.keys || ''
  const isSubList = props.listIndex && keys.indexOf(sourceKeys) === 0 && keys !== sourceKeys
  if (isSubList) {
    // 当前数组索引,是否为上一个索引key子项1
    return {
      listUUID: props.element.uuid,
      source: dataSource?.source[0] || '',
      index: `${props.listIndex.index},${index}`
    }
  } else if (!keys && props.listIndex?.index) {
    // 静态数据列表，嵌套列表
    return {
      listUUID: props.element.uuid,
      source: dataSource?.source[0] || '',
      index: `${props.listIndex.index},${index}`
    }
  }
  return {
    listUUID: props.element.uuid,
    source: dataSource?.source[0] || '',
    index: `${index}`
  }
}

/** 是否显示数据项 */
const showItems = (index) => {
  if (props.hiddenIndexs && props.hiddenIndexs.includes(index)) {
    return false
  }
  return true
}

/** 获取当前下标对应的列表项数据 */
const getListItemData = (index) => {
  if (Array.isArray(props.list)) {
    return props.list[index]
  }
  return undefined
}

const setStyle = (nodes) => {
  addListCss(props.element.uuid, props.element.style)
  if (!nodes || !nodes.length) {
    elements.value = []
    return
  }
  nodes.forEach((node) => {
    node.style = {
      ...node.style,
      position: 'absolute'
    }
  })
  elements.value = nodes
  // console.log('---setStyle:', elements.value)
}

const refreshList = () => {
  // TODO: 缺少测试cb实例数据量调优
  // 有数据源绑定, 但未获取数据源时不进行节点更新
  // 如是脚本处理的列表数据，则props.list为脚本处理后的数据，需反向更新数据源
  // 多层嵌套子列表, 根据props.listIndex.index自身的下标索引,保存数据源
  if (props.element.dataSource?.list?.type) {
    // 绑定了数据源，将列表数据挂在在全局中
    const sdUUID = `${props.element.uuid}${props?.listIndex?.index || ''}`
    let listClickData = appStore.getPageDataSource(sdUUID) || {}
    listClickData = Object.assign(listClickData, {
      listData: props.list || []
    })
    appStore.setPageDataSource(sdUUID, listClickData)
  }
  if (Array.isArray(props.list)) {
    listLength.value = props.list.length
  } else if (typeof props.list === 'string') {
    listLength.value = Number(props.list)
  } else {
    listLength.value = 0
  }
  refreshIndex.value = refreshIndex.value + 1
}

const clearSelect = () => {
  // 清空选中数据
  clearListClickDataSource(props.element.uuid)
}

/** 查找可滚动的父容器 */
const findScrollParent = (element) => {
  let parent = element?.parentElement
  while (parent && parent !== document.body) {
    const { overflowX, overflowY } = getComputedStyle(parent)
    const isScrollable =
      ['auto', 'scroll'].includes(overflowX) || ['auto', 'scroll'].includes(overflowY) || parent.classList.contains('xy-horizontal-layout') || parent.classList.contains('xy-vertical-layout')
    if (isScrollable) {
      return parent
    }
    parent = parent.parentElement
  }
  return null
}

/** 滚动到指定下标的列表项 */
const scrollToIndex = (index) => {
  const tryScroll = (retry = 0) => {
    nextTick(() => {
      const root = layoutRef.value
      if (!root || index < 0) return

      const items = root.querySelectorAll('.xy-list-item-layout__content')
      const target = items[index]
      if (!target) {
        if (retry < 8) tryScroll(retry + 1)
        return
      }

      const scrollParent = findScrollParent(target)
      if (!scrollParent) {
        target.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })
        return
      }

      const targetRect = target.getBoundingClientRect()
      const parentRect = scrollParent.getBoundingClientRect()
      const isHorizontal = props.direction === 'horizontal'

      if (isHorizontal) {
        const left = scrollParent.scrollLeft + (targetRect.left - parentRect.left)
        scrollParent.scrollTo({ left: Math.max(0, left), behavior: 'smooth' })
      } else {
        const top = scrollParent.scrollTop + (targetRect.top - parentRect.top)
        scrollParent.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
      }
    })
  }
  tryScroll()
}

watch(
  () => props.list,
  (newVal) => {
    console.log('列表数据刷新', newVal)
    refreshList()
  }
)

onBeforeMount(() => {
  setStyle(props.element.nodes)
  refreshList()
})

defineExpose({
  clearSelect,
  scrollToIndex
})
</script>

<style lang="scss" scoped>
.xy-list-item-layout {
  width: auto !important;
  height: auto !important;
}

.xy-list-item-layout--vertical {
  display: flex;
  flex-direction: column;
}

.xy-list-item-layout--horizontal {
  display: flex;
  flex-direction: row;
}

.xy-list-item-layout--grid {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
}

.xy-list-item-layout__content {
  position: relative;
  overflow: hidden;
}
</style>
